import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router";
import { useToast } from "@astrum/ui";
import { api, ApiError } from "../../lib/api.js";
import type { Channel } from "../../lib/api.js";
import { isAttending, onAttention } from "../../lib/attention.js";
import { useAuth } from "../../lib/auth.js";
import { closeSystemNotifications } from "../../lib/push.js";
import { useReconnected, useRealtimeFrames } from "../../lib/realtime-context.js";

export interface Channels {
  channels: Channel[];
  loading: boolean;
  setChannels: Dispatch<SetStateAction<Channel[]>>;
  /** Mark a channel read up to a message, once someone can actually see it. */
  markRead: (channelId: string, messageId: string) => void;
  /** A message landed in a channel: move its "last activity". */
  touch: (channelId: string, createdAt: string) => void;
  /** Pins or unpins a channel to the top of the viewer's own sidebar. */
  togglePin: (channel: Channel) => void;
  /** Re-reads the list without a loading state, e.g. after joining or archiving. */
  reload: () => void;
}

/**
 * The conversation list: loading it, keeping unread badges live, marking the
 * open channel read, and refreshing after a reconnect.
 */
export function useChannels(openChannelId: string | undefined): Channels {
  const { viewer } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(true);

  /**
   * The newest message in a channel that arrived while nobody was looking,
   * held until the window has attention again. See `markRead`.
   */
  const pendingReadRef = useRef<{ channelId: string; messageId: string } | null>(null);

  /**
   * Mark a channel read up to `messageId` — but only once someone can see it.
   *
   * Being on the route is not reading: the tab may be in the background or
   * the desktop window hidden in the tray. Marking read then cleared the
   * notification (and the popup's reason to exist) before the person had
   * seen anything, so it waits for attention and is sent then instead.
   */
  const markRead = useCallback((readChannelId: string, messageId: string) => {
    if (!isAttending()) {
      pendingReadRef.current = { channelId: readChannelId, messageId };
      return;
    }
    pendingReadRef.current = null;
    // Fire-and-forget: marking read failing shouldn't block viewing the thread.
    api.post(`/channels/${readChannelId}/read`, { lastReadMessageId: messageId }).catch(() => undefined);
    setChannels((prev) => prev.map((c) => (c.id === readChannelId && c.unreadCount ? { ...c, unreadCount: 0 } : c)));
    void closeSystemNotifications([`channel:${readChannelId}`]);
  }, []);

  // A read held back while the window was out of view goes out as soon as it
  // is back — provided it is still the conversation on screen.
  useEffect(
    () =>
      onAttention(() => {
        const pending = pendingReadRef.current;
        if (pending && pending.channelId === openChannelId) markRead(pending.channelId, pending.messageId);
      }),
    [openChannelId, markRead],
  );

  /**
   * Fetches the conversation list. `quiet` is the reconnect refresh: the list
   * is already on screen, so it neither shows the loading state nor toasts a
   * failure — the stale list stays, and the next reconnect tries again.
   */
  const loadChannels = useCallback(
    (quiet = false) => {
      if (!quiet) setLoading(true);
      return api
        .get<{ channels: Channel[] }>("/channels")
        .then((res) => setChannels(res.channels))
        .catch((error: unknown) => {
          if (!quiet) toast(error instanceof ApiError ? error.message : "Could not load channels.", { tone: "danger" });
        })
        .finally(() => {
          if (!quiet) setLoading(false);
        });
    },
    [toast],
  );

  useEffect(() => {
    void loadChannels();
  }, [viewer]);

  // Back after a drop: unread counts and new channels changed unseen.
  useReconnected(() => {
    void loadChannels(true);
  });

  const touch = useCallback((channelId: string, createdAt: string) => {
    setChannels((prev) => prev.map((c) => (c.id === channelId ? { ...c, lastMessageAt: createdAt } : c)));
  }, []);

  useRealtimeFrames((frame) => {
    switch (frame.t) {
      case "message": {
        // The open channel's messages are useChannelMessages'; it reports them back via touch/markRead.
        if (frame.message.channelId === openChannelId) return;
        // Not the open channel: bump its unread badge instead.
        setChannels((prev) =>
          prev.map((c) =>
            c.id === frame.message.channelId
              ? { ...c, unreadCount: (c.unreadCount ?? 0) + 1, lastMessageAt: frame.message.createdAt }
              : c,
          ),
        );
        return;
      }

      case "read": {
        // Read on another device or tab: this one's badge for it goes too.
        if (frame.userId === viewer?.id) {
          setChannels((prev) =>
            prev.map((c) => (c.id === frame.channelId && c.unreadCount ? { ...c, unreadCount: 0 } : c)),
          );
        }
        return;
      }

      case "channel_gone": {
        setChannels((prev) => prev.filter((c) => c.id !== frame.channelId));
        if (frame.channelId === openChannelId) {
          toast("This channel was deleted.", { tone: "danger" });
          navigate("/chat", { replace: true });
        }
        return;
      }

      case "channel_new": {
        /*
         * The frame carries an id, not a channel — it is a signal that access
         * changed, and the socket has already subscribed to the topic by the
         * time this arrives. Fetching gets the name, unread count and member
         * count the sidebar needs, which no frame should be duplicating.
         */
        void api
          .get<{ channels: Channel[] }>("/channels")
          .then((res) => setChannels(res.channels))
          // Silent: the channel is already live on the socket, so the only
          // cost of a failed refresh is that the sidebar lags until the next
          // load. A toast about it would be noise.
          .catch(() => undefined);
        return;
      }

      case "channel_updated": {
        // Archived, unarchived, retopiced or renamed by someone: the list
        // carries the archive flag and hides archived channels, so refetch.
        void loadChannels(true);
        return;
      }

      default:
        return;
    }
  });

  /**
   * Optimistic, then corrected: a stale pin state left showing after a failed
   * request is a smaller problem than a click that visibly does nothing until
   * the round trip completes.
   */
  const togglePin = useCallback(
    (channelToggled: Channel) => {
      const pinning = !channelToggled.pinnedAt;
      const optimistic = pinning ? new Date().toISOString() : null;
      setChannels((prev) => prev.map((c) => (c.id === channelToggled.id ? { ...c, pinnedAt: optimistic } : c)));
      api
        .patch<{ pinnedAt: string | null }>(`/channels/${channelToggled.id}/pin`, { pinned: pinning })
        .then((res) => {
          setChannels((prev) => prev.map((c) => (c.id === channelToggled.id ? { ...c, pinnedAt: res.pinnedAt } : c)));
        })
        .catch((error: unknown) => {
          setChannels((prev) =>
            prev.map((c) => (c.id === channelToggled.id ? { ...c, pinnedAt: channelToggled.pinnedAt } : c)),
          );
          toast(error instanceof ApiError ? error.message : "Could not change that.", { tone: "danger" });
        });
    },
    [toast],
  );

  const reload = useCallback(() => {
    void loadChannels(true);
  }, [loadChannels]);

  return { channels, loading, setChannels, markRead, touch, togglePin, reload };
}
